// src/components/Dashboard.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
} from "@mui/material";
import { motion } from "framer-motion";
import Nav from "./Nav";
import styles from "./Dashbrd.module.css";

// Icons
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SchoolIcon from "@mui/icons-material/School";
import FactoryIcon from "@mui/icons-material/Factory";
import PublicIcon from "@mui/icons-material/Public";
import GavelIcon from "@mui/icons-material/Gavel";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

// Images
import universalImg from "../../asset/dashb/universal.png";
import otImg from "../../asset/dashb/otImg.png";
import educationImg from "../../asset/dashb/educationImg.png";
import healthaiImg from "../../asset/dashb/healthImg.png";
import patientImg from "../../asset/dashb/patientImg.png";
import pharmacyImg from "../../asset/dashb/pharmacyImg.png";
import industrialImg from "../../asset/dashb/industrialImg.webp";
import chemicalImg from "../../asset/dashb/chemicalImg.webp";
import classImg from "../../asset/dashb/classImg.png";
import cocImg from "../../asset/dashb/codeofconduct.png";
import taxImg from "../../asset/dashb/taxImg.png";

/* ================= CARD COMPONENT ================= */
const CardComponent = ({ title, description, onDemoClick, logo }) => (
  <motion.div 
    className={styles.cardWrapper}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Card className={styles.card}>
      {/* Card accent line */}
      <div className={styles.cardAccent}></div>
      
      <CardContent className={styles.cardContent}>
        {/* Icon container with subtle glow */}
        <div className={styles.iconContainer}>
          {logo && (
            <img src={logo} alt={title} className={styles.cardImage} />
          )}
        </div>

        <Typography variant="h5" className={styles.cardTitle}>
          {title}
        </Typography>

        <Typography variant="body2" className={styles.cardDesc}>
          {description}
        </Typography>

        <Button 
          className={styles.cardButton} 
          onClick={onDemoClick}
          variant="outlined"
        >
          Try Demo
        </Button>
      </CardContent>
    </Card>
  </motion.div>
);

const Dashbrd = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const welcomeMessage = location.state?.welcomeMessage;
  const username = location.state?.username;

  const [activeSection, setActiveSection] = useState("Universal");
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);

  useEffect(() => {
    if (welcomeMessage) {
      toast.success(welcomeMessage, {
        position: "top-center",
        autoClose: 2000,
        theme: "dark",
      });
    }
  }, [welcomeMessage]);

  const handleDemoClick = (route) => navigate(route);
  const toggleSidebar = () => setIsSidebarExpanded((prev) => !prev);

  const getCards = () => {
    switch (activeSection) {
      case "Universal":
        return (
          <CardComponent
            title="Universal AI"
            description="Explore AI solutions tailored for universal applications."
            logo={universalImg}
            onDemoClick={() => handleDemoClick("/demochat")}
          />
        );

      case "Healthcare":
        return (
          <>
            <CardComponent title="Operation Theater" description="Manage surgical operations efficiently." logo={otImg} onDemoClick={() => handleDemoClick("/demochat")} />
            <CardComponent title="Patient Health" description="Track patient health records." logo={patientImg} onDemoClick={() => handleDemoClick("/demochat")} />
            <CardComponent title="Clinical Module" description="Access clinical data." logo={healthaiImg} onDemoClick={() => handleDemoClick("/demochat")} />
            <CardComponent title="Pharmacy" description="Manage pharmacy inventory." logo={pharmacyImg} onDemoClick={() => handleDemoClick("/demochat")} />
          </>
        );

      case "Education":
        return (
          <>
            <CardComponent title="Admission" description="Student admission management." logo={educationImg} onDemoClick={() => handleDemoClick("/demochat")} />
            <CardComponent title="My Class" description="Class schedules & resources." logo={classImg} onDemoClick={() => handleDemoClick("/demochat")} />
          </>
        );

      case "Industrial":
        return (
          <>
            <CardComponent title="My Industry" description="Industry insights." logo={industrialImg} onDemoClick={() => handleDemoClick("/demochat")} />
            <CardComponent title="Chemicals" description="Chemical inventory & safety." logo={chemicalImg} onDemoClick={() => handleDemoClick("/demochat")} />
          </>
        );

      case "COC":
        return (
          <CardComponent
            title="Code of Conduct"
            description="Maintain respectful workplace policies."
            logo={cocImg}
            onDemoClick={() => handleDemoClick("/codeofconduct")}
          />
        );

      case "IncomeTax":
        return (
          <CardComponent
            title="Income Tax Department"
            description="Manage income tax filings."
            logo={taxImg}
            onDemoClick={() => handleDemoClick("/demochat")}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.dashboard}>
      <ToastContainer />
      <Nav username={username} />

      {/* SIDEBAR */}
      <aside
        className={`${styles.sidebar} ${
          isSidebarExpanded ? styles.expanded : styles.collapsed
        }`}
      >
        {/* Sidebar title */}
        <div className={styles.sidebarTitle}>
          <span className={styles.sidebarTitleText}>
            AI MODULES
          </span>
        </div>

        <List className={styles.sidebarList}>
          {[
            { label: "Universal", icon: <PublicIcon /> },
            { label: "Healthcare", icon: <LocalHospitalIcon /> },
            { label: "Education", icon: <SchoolIcon /> },
            { label: "Industrial", icon: <FactoryIcon /> },
            { label: "COC", icon: <GavelIcon /> },
            { label: "IncomeTax", icon: <AccountBalanceIcon /> },
          ].map((item) => (
            <ListItem
              button
              key={item.label}
              onClick={() => setActiveSection(item.label)}
              className={`${styles.listItem} ${
                activeSection === item.label ? styles.activeItem : ""
              }`}
            >
              <ListItemIcon className={styles.listIcon}>
                {item.icon}
              </ListItemIcon>

              {isSidebarExpanded && (
                <ListItemText
                  primary={
                    <span className={styles.listText}>
                      {item.label === "COC"
                        ? "Code of Conduct"
                        : item.label === "IncomeTax"
                        ? "Income Tax Dept"
                        : item.label}
                    </span>
                  }
                />
              )}
            </ListItem>
          ))}
        </List>

        <div 
          className={styles.toggleButton} 
          onClick={toggleSidebar}
        >
          {isSidebarExpanded ? <ArrowBackIosIcon /> : <ArrowForwardIosIcon />}
        </div>
      </aside>

      {/* CONTENT */}
      <Box className={styles.content}>
        {/* Section title */}
        <div className={styles.sectionTitle}>
          <h2 className={styles.sectionTitleText}>
            {activeSection} <span className={styles.titleHighlight}>Modules</span>
          </h2>
          {/* <p className={styles.sectionSubtitle}>
            Select a module to explore AI-powered solutions
          </p> */}
        </div>
        
        <div className={styles.cardsContainer}>
          {getCards()}
        </div>
      </Box>
    </div>
  );
};

export default Dashbrd;